/**
 * Redirect handling built on MSAL v5's redirect bridge: the directory returns to /redirect, where
 * broadcastResponseToMainFrame caches the response in sessionStorage and navigates back to the
 * page that started the sign-in, so the response never rides a URL the hash router can destroy.
 * The bridge page is wired in each application's main.ts, instead of bootstrapping Angular; the
 * login page collects the response. Background and alternatives considered:
 * CLAUDE/specs/CLAUDE-entra-signin-spec.md, "Why the redirect leg is assembled this way".
 *
 * The response comes back in the fragment rather than the query string. A fragment is never sent
 * to a server, which keeps the authorization code out of the front door in front of every
 * deployed environment: its command-injection rule refuses the directory's own clientdata value,
 * which carries consecutive pipes. Fragment mode is safe here only because no router runs on the
 * bridge path, so there is no #/route for the response fragment to collide with.
 */
import type { AuthenticationResult, Configuration, PublicClientApplication } from '@azure/msal-browser';
import { EntraStaffAuthConfig } from '@raptor-fe/core';

/**
 * The path the directory returns to. Served as the application shell by the SPA fallback, but
 * main.ts runs the bridge instead of bootstrapping Angular there, so no router ever starts on it.
 * Must match the SPA reply URLs on the app registration exactly.
 */
const REDIRECT_BRIDGE_PATH = '/redirect';

/**
 * Survives the round trip to the directory in the same tab, unlike any in-memory state. Without
 * it every page load would have to load msal and call handleRedirectPromise just in case.
 */
const PENDING_INTERACTION_KEY = 'raptor.staff-sign-in.pending';

let configuration: EntraStaffAuthConfig | null = null;
let client: Promise<PublicClientApplication> | null = null;
let redirectHandled = false;

/**
 * Resets the memoised client alongside the configuration, so a later call cannot leave a live
 * instance built from a registration the module no longer reports.
 */
export function configureStaffAuth(config: EntraStaffAuthConfig | null): void {
    if (config && !(config.tenantId && config.clientId)) {
        console.error('Staff sign-in is enabled but the Entra registration is incomplete: tenantId and clientId are both required.');
    }

    if (config?.tenantId && !isDirectoryId(config.tenantId)) {
        console.error(
            'Staff sign-in is disabled: the configured tenant is not a directory id. The multi-tenant authorities widen the population the directory will authenticate.',
        );
        configuration = null;
        client = null;
        redirectHandled = false;
        return;
    }

    configuration = config?.tenantId && config?.clientId ? config : null;
    client = null;
    redirectHandled = false;
}

export function isStaffAuthConfigured(): boolean {
    return configuration !== null;
}

/** Whether this page load is the directory's return leg and must run the bridge, not the app. */
export function isRedirectBridgeRequest(): boolean {
    return configuration !== null && typeof window !== 'undefined' && window.location.pathname === REDIRECT_BRIDGE_PATH;
}

/**
 * Runs the redirect bridge page. On the redirect flow the bridge caches the response and
 * navigates back to the initiating page itself; this page is abandoned either way, so a failure
 * (a stray visit with nothing to parse) leaves for the home page rather than a blank shell.
 * The navigation seam exists because jsdom forbids stubbing window.location.
 */
export async function runRedirectBridge(navigate: (url: string) => void = (url) => window.location.replace(url)): Promise<void> {
    try {
        const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');
        await broadcastResponseToMainFrame();
    } catch (error) {
        console.error('The Microsoft sign-in response could not be relayed.', error);
        navigate(window.location.origin);
    }
}

/**
 * Memoised so every caller shares one instance and one initialize(); msal-browser is imported
 * dynamically so apps that never sign staff in do not carry it.
 */
export function staffAuthClient(): Promise<PublicClientApplication> {
    if (!configuration) {
        return Promise.reject(new Error('Staff sign-in is not configured for this application.'));
    }

    if (!client) {
        client = createClient(configuration);
    }

    return client;
}

/**
 * Whether the value names one directory. `common`, `organizations` and `consumers` are the values the
 * quickstart documentation uses, and each authenticates a wider population than this registration's
 * own directory.
 */
function isDirectoryId(tenantId: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenantId);
}

async function createClient(config: EntraStaffAuthConfig): Promise<PublicClientApplication> {
    const msal = await import('@azure/msal-browser');

    const options: Configuration = {
        auth: {
            clientId: config.clientId,
            authority: `https://login.microsoftonline.com/${config.tenantId}`,
            redirectUri: config.redirectUri || `${window.location.origin}${REDIRECT_BRIDGE_PATH}`,
            OIDCOptions: { responseMode: msal.ResponseMode.FRAGMENT },
        },
        cache: { cacheLocation: 'sessionStorage' },
    };

    const instance = new msal.PublicClientApplication(options);
    await instance.initialize();
    return instance;
}

function apiScope(config: EntraStaffAuthConfig): string {
    return `api://${config.clientId}/access_as_user`;
}

export async function beginStaffSignIn(): Promise<void> {
    if (!configuration) {
        throw new Error('Staff sign-in is not configured for this application.');
    }

    const instance = await staffAuthClient();
    try {
        window.sessionStorage.setItem(PENDING_INTERACTION_KEY, '1');
        await instance.loginRedirect({ scopes: [apiScope(configuration)], prompt: 'select_account' });
    } catch (error) {
        window.sessionStorage.removeItem(PENDING_INTERACTION_KEY);
        throw error;
    }
}

function hasPendingStaffInteraction(): boolean {
    try {
        return window.sessionStorage.getItem(PENDING_INTERACTION_KEY) === '1';
    } catch {
        return false;
    }
}

/**
 * Completes the redirect leg by collecting the response the bridge cached, yielding the staff
 * token, or null when no sign-in left this tab. Once per page load: while an interaction is in
 * progress a call that finds no cached response resets the cached request, discarding the PKCE
 * verifier a concurrent call would need.
 */
export async function completeStaffSignIn(): Promise<string | null> {
    if (!configuration || redirectHandled || !hasPendingStaffInteraction()) {
        return null;
    }

    redirectHandled = true;

    try {
        const instance = await staffAuthClient();
        const result: AuthenticationResult | null = await instance.handleRedirectPromise({
            navigateToLoginRequestUrl: false,
        });

        const token = result?.accessToken ?? null;

        if (!token) {
            throw new Error('Microsoft did not return a sign-in response. Please try again.');
        }

        return token;
    } finally {
        window.sessionStorage.removeItem(PENDING_INTERACTION_KEY);
    }
}

/**
 * Renews the staff token for the identity the Raptor session belongs to, named by its directory
 * object id. The cache is insertion-ordered, so choosing from it would renew as whichever staff
 * member the browser happens to list first: the API would issue a valid session for them while the
 * interface still shows the original person, and the access log would name the wrong principal from
 * then on. Without an object id the single cached account is used and an ambiguous cache is refused.
 */
export async function acquireStaffToken(directoryObjectId?: string): Promise<string> {
    if (!configuration) {
        throw new Error('Staff sign-in is not configured for this application.');
    }

    const instance = await staffAuthClient();
    const accounts = instance.getAllAccounts();

    if (accounts.length === 0) {
        throw new Error('No Entra account is present for a silent staff token renewal.');
    }

    const account = directoryObjectId
        ? accounts.find((candidate) => candidate.localAccountId === directoryObjectId)
        : accounts.length === 1
          ? accounts[0]
          : undefined;

    if (!account) {
        throw new Error('No cached Entra account matches the staff session; refusing to renew as anyone else.');
    }

    const result = await instance.acquireTokenSilent({ account, scopes: [apiScope(configuration)] });
    return result.accessToken;
}

/**
 * Clears the local Entra cache only. The directory session is deliberately left alone so signing
 * out of Raptor does not sign the person out of every other Megabus application.
 */
export async function endStaffSession(): Promise<void> {
    if (!configuration) {
        return;
    }

    const instance = await staffAuthClient();
    await instance.clearCache();
}
